<template>
  <div class="col-xs-12">
    <div id="customers-panel" class="panel panel-primary">
      <div class="panel-heading" style="text-align: left">Add New Product</div>

      <form enctype="multipart/form-data" @submit.prevent="addProductForm">
        <div class="panel-body">
          <div class="form-group">
            <label class="control-label">Product Label*</label>
            <input
              v-model="label"
              class="form-control"
              type="text"
              name="label"
              value=""
              required
            />
          </div>

          <div class="form-group">
            <label class="control-label">Nuance Sku*</label>
            <input
              v-model="sku"
              class="form-control"
              type="text"
              name="sku"
              value=""
              required
            />
          </div>

          <div class="form-group">
            <label for="locale_id">Locale*</label>
            <select
              id="locale_id"
              v-model="locale_id"
              type="text"
              name="locale_id"
              class="form-control"
              required
            >
              <option
                v-for="locale in locales"
                :key="'locale-' + locale.id"
                :value="locale.id"
              >
                {{ locale.country }}
              </option>
            </select>
          </div>

          <div class="row">
            <div class="form-group col-xs-12">
              <label class="control-label">Base Price*</label>
              <div class="input-group">
                <span class="input-group-addon">
                  <span class="glyphicon glyphicon-usd"></span>
                </span>
                <input
                  v-model="base_price"
                  class="form-control"
                  type="number"
                  min="0"
                  step="0.01"
                  name="base_price"
                  value="0.01"
                  required
                  @change="handleBasePrice($event)"
                />
              </div>
              <span class="help-block">
                Exclude any addon and/or discount prices
              </span>
            </div>

            <!-- ====== ITEM TYPE SELECT ====== -->

            <div class="form-group col-xs-12">
              <label class="control-label">Item Type*</label>
              <select
                v-model="item_type"
                class="form-control"
                name="item_type"
                @change.prevent="itemTypeChange($event)"
              >
                <option value="product">Product</option>
                <option value="renewal">Renewal</option>
              </select>

              <div id="special_select" class="well" :class="itemType">
                <div class="row">
                  <div class="form-group col-xs-12">
                    <select
                      v-model="product_ref"
                      class="form-control"
                      name="product_ref"
                      @change="handleProductSelect($event)"
                    >
                      <option value="">-- Select Original Product --</option>
                      <option
                        v-for="product in products"
                        :key="'product-' + product.id"
                        :value="product.id"
                      >
                        {{ product.label }}
                      </option>
                    </select>
                  </div>

                  <div class="form-group col-xs-12">
                    <select
                      v-model="product_discounts"
                      class="form-control"
                      name="product_discounts"
                      @change="handleDiscountSelect($event)"
                    >
                      <option value="">-- Select Discount --</option>
                      <option
                        v-for="discount in discounts"
                        :key="'discount-' + discount.id"
                        :value="discount.id"
                        :duration="discount.duration"
                        :amount="discount.amount"
                      >
                        {{ discount.code }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="clearfix"></div>
            </div>

            <div class="form-group col-xs-12">
              <label class="control-label">Group*</label>
              <select
                v-model="group"
                class="form-control"
                name="group"
                required
              >
                <option
                  v-for="groupItem in groups"
                  :key="'group-' + groupItem.name"
                  :value="groupItem.name"
                >
                  {{ groupItem.name }}
                </option>
              </select>
            </div>

            <div class="form-group col-xs-12">
              <label class="control-label">Nexus Type*</label>
              <select
                v-model="nexus"
                class="form-control"
                name="nexus"
                required
              >
                <option value="saas">SaaS Products</option>
                <option value="profs">Professional Services</option>
                <option value="hardware">Hardware & Boxed Software</option>
                <option value="digital">Digital Download</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label class="control-label">Description*</label>
            <editor
              v-model="description"
              api-key="qikbpk8rdl84ppg4fbhzx8x623m4v13szu2vyqnrac209vbg"
              :init="{
                plugins: ['link image code lists'],
                height: '300px',
                toolbar:
                  'undo redo| styleselect | bold italic | link image | alignleft aligncenter alignright alignjustify alignnone | numlist bullist | code |',
              }"
            >
            </editor>
          </div>

          <div class="form-group">
            <label class="control-label">Terms*</label>
            <editor
              v-model="terms"
              api-key="qikbpk8rdl84ppg4fbhzx8x623m4v13szu2vyqnrac209vbg"
              :init="{
                plugins: ['link image code lists'],
                height: '400px',
                toolbar:
                  'undo redo| styleselect | bold italic | link image | alignleft aligncenter alignright alignjustify alignnone | numlist bullist | code |',
              }"
            >
            </editor>
          </div>

          <div class="form-group">
            <label class="control-label">Product Image</label>
            <input
              class="form-control"
              type="file"
              name="image"
              accept="image/*"
              @change="handleImageFile($event)"
            />
          </div>

          <hr />

          <!-- ====== SUBSCRIPTION SELECT ====== -->

          <div class="checkbox" style="margin-top: 0; margin-bottom: 0">
            <label>
              <input
                type="checkbox"
                :disabled="subscriptionDisabled"
                :checked="subscriptionChecked"
                name="has_subscription"
                :value="subscriptionValue"
                @change="handleSubscription($event)"
              />
              Has Subscription
            </label>
          </div>

          <div
            id="subscription_select"
            class="well"
            :class="subscriptionValue ? null : 'hidden'"
          >
            <div class="row">
              <div class="form-group col-xs-12">
                <label>Max Cycles*</label>
                <input
                  ref="max_cycles"
                  class="form-control"
                  type="number"
                  name="max_cycles"
                  min="1"
                  step="1"
                  :disabled="subscriptionDisabled"
                  :value="subscriptionData.max_cycles"
                  @change="handleSubsFieldsChange()"
                />
              </div>

              <div class="form-group col-xs-12">
                <label>Cycle Period*</label>
                <select
                  ref="period"
                  :disabled="subscriptionDisabled"
                  class="form-control"
                  name="period"
                  @change="handleSubsFieldsChange()"
                >
                  <option
                    value="month"
                    :selected="subscriptionData.period === 'month'"
                  >
                    Month
                  </option>
                  <option
                    value="year"
                    :selected="subscriptionData.period === 'year'"
                  >
                    Year
                  </option>
                  <option
                    value="2year"
                    :selected="subscriptionData.period === '2year'"
                  >
                    2 Years
                  </option>
                  <option
                    value="3year"
                    :selected="subscriptionData.period === '3year'"
                  >
                    3 Years
                  </option>
                </select>
              </div>
              <div class="clearfix"></div>
            </div>
          </div>

          <hr />

          <!-- ====== ECLIPSE PRODUCTS ====== -->

          <div class="form-group">
            <label class="control-label">Eclipse Product Data</label>
            <div class="well">
              <div class="row">
                <div class="form-group col-xs-6" style="margin-bottom: 0">
                  <label class="control-label">Product*</label>
                </div>

                <div
                  v-for="(eclipseProduct, index) in eclipseProducts"
                  :key="'eclipse_product_' + index"
                  class="eclipse_product_container"
                >
                  >
                  <button
                    v-if="index > 0"
                    class="close"
                    type="button"
                    style="padding-right: 15px; padding-bottom: 5px"
                    @click="removeProduct(index)"
                  >
                    <span aria-hidden="true">x</span>
                  </button>

                  <div class="form-group col-xs-12">
                    <input
                      autocomplete="off"
                      type="text"
                      name="eclipse_product[]"
                      :value="eclipseProduct.product_name"
                      class="form-control"
                      @click="
                        modal(true, 'eclipseProduct', 'Search Eclipse Product');
                        setIndexModal(index);
                      "
                    />
                  </div>
                  <div class="form-group col-xs-12">
                    <select
                      class="form-control"
                      name="product_implementation"
                      required
                      @change="handleUpdateTotal($event, index)"
                    >
                      <option value="">-- Select Implementation --</option>
                      <option value="ces">CES</option>
                      <option value="var">VAR</option>
                    </select>
                  </div>

                  <div
                    class="eclipse_sub_cyclesX"
                    :class="subscriptionValue ? null : 'hidden'"
                  >
                    <div class="form-group col-xs-6">
                      <input
                        class="form-control"
                        type="number"
                        name="product_cycle_start"
                        min="1"
                        placeholder="Cycle Start..."
                        @change="handleUpdateTotal($event, index)"
                      />
                    </div>
                    <div class="form-group col-xs-6">
                      <input
                        class="form-control"
                        type="number"
                        name="product_cycle_end"
                        min="1"
                        placeholder="Cycle End..."
                        @change="handleUpdateTotal($event, index)"
                      />
                    </div>
                  </div>

                  <div class="form-group col-xs-12">
                    <div class="row">
                      <div class="form-group col-xs-12 col-sm-6">
                        <label>Customer Cost</label>
                        <div class="input-group">
                          <span class="input-group-addon">
                            <span class="glyphicon glyphicon-usd"></span>
                          </span>
                          <input
                            class="form-control"
                            type="text"
                            name="cycle_cost_unused"
                            step="0.01"
                            :value="eclipseProduct.cycle_cost_unused"
                            disabled
                          />
                        </div>
                      </div>
                    </div>

                    <div class="form-group col-xs-12 col-sm-6">
                      <label>Vendor Profit</label>
                      <div class="input-group">
                        <span class="input-group-addon">
                          <span class="glyphicon glyphicon-usd"></span>
                        </span>
                        <input
                          class="form-control"
                          type="number"
                          name="vendor_cost_unused[]"
                          step="0.01"
                          :value="
                            formatAmount(eclipseProduct.vendor_cost_unused)
                          "
                          disabled
                        />
                      </div>
                    </div>

                    <div class="form-group col-xs-12 col-sm-4">
                      <label>eDist Profit</label>
                      <div class="input-group">
                        <span class="input-group-addon">
                          <span class="glyphicon glyphicon-usd"></span>
                        </span>
                        <input
                          class="form-control"
                          type="number"
                          name="edist_cut"
                          step="0.01"
                          value="0"
                          @change="handleUpdateTotal($event, index)"
                        />
                      </div>
                    </div>

                    <div class="form-group col-xs-12 col-sm-4">
                      <label>Var Profit</label>
                      <div class="input-group">
                        <span class="input-group-addon">
                          <span class="glyphicon glyphicon-usd"></span>
                        </span>
                        <input
                          class="form-control"
                          type="number"
                          name="var_cut"
                          step="0.01"
                          value="0"
                          @change="handleUpdateTotal($event, index)"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="form-group col-xs-12 col-sm-4">
                    <label>CESC Profit</label>
                    <div class="input-group">
                      <span class="input-group-addon">
                        <span class="glyphicon glyphicon-usd"></span>
                      </span>
                      <input
                        class="form-control"
                        type="number"
                        name="ces_cut"
                        step="0.01"
                        value="0"
                        @change="handleUpdateTotal($event, index)"
                      />
                    </div>
                  </div>

                  <div class="clearfix"></div>
                </div>
              </div>

              <div class="clearfix"></div>
              <hr />
              <div class="clearfix"></div>
              <div>
                <div>
                  <div class="col-xs-12">
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click="addEclipseProduct()"
                    >
                      + Add Another Product
                    </button>
                  </div>
                </div>
                <div class="clearfix"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <button id="form_submit" type="submit" class="btn btn-primary">
            <span v-if="loadingSubmit === null || loadingSubmit === false">
              Submit
            </span>
            <span v-else-if="loadingSubmit === true">Processing...</span>
          </button>
        </div>
      </form>

      <modal v-if="modalId === 'eclipseProduct'" :show-modal="showModal">
        <eclipse-product-search></eclipse-product-search>
      </modal>
    </div>
  </div>
</template>

<script>
import Editor from "@tinymce/tinymce-vue";
import Modal from "../../../Modal.vue";
import EclipseProductSearch from "../forms/eclipseProductSearch.vue";

export default {
  components: {
    Editor,
    Modal,
    EclipseProductSearch,
  },
  data() {
    return {
      label: "",
      sku: "",
      base_price: "",
      locale_id: "",
      item_type: "",
      product_ref: "",
      product_discounts: "",
      group: "",
      nexus: "",
      description: "",
      terms: "",
      tiny_key: process.env.MIX_TINY_KEY,
    };
  },
  computed: {
    locales() {
      return this.$store.state.dic.locales;
    },
    loadingSubmit() {
      return this.$store.state.product.loading_submit_form;
    },
    itemType() {
      return this.$store.state.product.item_type;
    },
    products() {
      return this.$store.state.product.products;
    },
    productImage() {
      return this.$store.state.product.product_image;
    },
    discounts() {
      return this.$store.state.product.discounts;
    },
    groups() {
      return this.$store.state.product.groups;
    },
    subscriptionValue() {
      return this.$store.state.product.subscription_value;
    },
    subscriptionChecked() {
      return this.$store.state.product.subscription_checked;
    },
    subscriptionDisabled() {
      return this.$store.state.product.subscription_disabled;
    },
    subscriptionData() {
      return this.$store.state.product.subscription_cycle_period;
    },
    vendorCostUnused() {
      return this.$store.state.product.vendor_cost_unused;
    },
    eclipseProducts() {
      return this.$store.state.product.eclipse_products;
    },
    showModal() {
      return this.$store.state.dic.show_modal;
    },
    modalId() {
      return this.$store.state.dic.modal_id;
    },
    maxCycles() {
      return this.$store.state.product.subscription_cycle_period.max_cycles;
    },
    period() {
      return this.$store.state.product.subscription_cycle_period.period;
    },
    groupEclipseProductId() {
      return this.$store.state.product.group_eclipse_product_id;
    },
    groupEclipseProductName() {
      return this.$store.state.product.group_eclipse_product_name;
    },
    groupEclipseProductImplementation() {
      return this.$store.state.product.group_eclipse_product_implementation;
    },
    groupEclipseProductCycleStart() {
      return this.$store.state.product.group_eclipse_product_cycle_start;
    },
    groupEclipseProductCycleEnd() {
      return this.$store.state.product.group_eclipse_product_cycle_end;
    },
    groupEclipseCycleCostUnused() {
      return this.$store.state.product.group_eclipse_product_cycle_cost_unused;
    },
    groupEclipseVendorCostUnused() {
      return this.$store.state.product.group_eclipse_product_vendor_cost_unused;
    },
    groupEclipseEdistCut() {
      return this.$store.state.product.group_eclipse_product_edist_cut;
    },
    groupEclipseVarCut() {
      return this.$store.state.product.group_eclipse_product_var_cut;
    },
    groupEclipseCesCut() {
      return this.$store.state.product.group_eclipse_product_ces_cut;
    },
  },
  created() {
    this.$store.dispatch("getGroups");
    this.$store.dispatch("getLocales");
    this.$store.dispatch("getProducts");
    this.$store.dispatch("getDiscounts");
  },
  mounted() {
    this.$store.dispatch("setInitialProduct");
  },
  methods: {
    formatAmount(value) {
      return value.toFixed(2);
    },
    addProductForm() {
      this.$store.dispatch("groupEclipseProductData");
      const formFields = this.getFormFields();
      this.$store.dispatch("submitAddNewProductForm", formFields);
    },
    getFormFields() {
      return {
        label: this.label,
        sku: this.sku,
        locale_id: this.locale_id,
        item_type: this.item_type,
        base_price: this.base_price,
        product_ref: this.product_ref,
        product_discounts: this.product_discounts,
        group: this.group,
        nexus: this.nexus,
        description: this.description,
        terms: this.terms,
        eclipse_product: this.groupEclipseProductId,
        eclipse_product_name: this.groupEclipseProductName,
        product_implementation: this.groupEclipseProductImplementation,
        product_cycle_start: this.groupEclipseProductCycleStart,
        product_cycle_end: this.groupEclipseProductCycleEnd,
        cycle_cost_unused: this.groupEclipseCycleCostUnused,
        vendor_cost_unused: this.groupEclipseVendorCostUnused,
        edist_cut: this.groupEclipseEdistCut,
        var_cut: this.groupEclipseVarCut,
        ces_cut: this.groupEclipseCesCut,
        has_subscription: this.subscriptionValue,
        max_cycles: this.maxCycles,
        period: this.period,
        image: this.productImage,
      };
    },
    modal(modalState, modalId, modalTitle) {
      this.$store.dispatch("clearEclipseSearchResults");
      this.$store.dispatch("setModal", {
        modal_id: modalId,
        modal_title: modalTitle,
      });
      this.$store.dispatch("setModalState", modalState);
    },
    setIndexModal(index) {
      this.$store.dispatch("setIndexModal", index);
    },
    itemTypeChange(event) {
      this.$store.dispatch("itemTypeChanged", event.target.value);
    },
    handleSubscription(event) {
      this.$store.dispatch("switchSubscription", event.target.value);
    },
    handleBasePrice(event) {
      this.$store.dispatch("setBasePrice", event.target.value);
      this.$store.dispatch("updateTotals");
    },
    handleProductSelect(event) {
      const productId = event.target.value;
      this.$store.dispatch("loadSubscription", productId);
    },
    handleDiscountSelect(event) {
      const index = event.target.options.selectedIndex;
      const data = {
        value: event.target.value,
        duration: event.target.options[index].getAttribute("duration"),
        amount: event.target.options[index].getAttribute("amount"),
      };

      this.$store.dispatch("setSubscriptionDurationAmount", data);
      this.$store.dispatch("updateTotals");
    },
    handleSubsFieldsChange() {
      this.$store.dispatch("setMaxCyclesPeriod", {
        max_cycles: this.$refs.max_cycles.value,
        period: this.$refs.period.value,
      });
      this.$store.dispatch("updateTotals");
    },
    handleUpdateTotal(event, index) {
      const field = event.target.getAttribute("name");
      const { value } = event.target;

      switch (field) {
        case "edist_cut":
          this.$store.dispatch("setEdistCutValue", { value, index });
          break;
        case "var_cut":
          this.$store.dispatch("setVarCutValue", { value, index });
          break;
        case "ces_cut":
          this.$store.dispatch("setCesCutValue", { value, index });
          break;
        case "product_implementation":
          this.$store.dispatch("setProdImplementationValue", { value, index });
          break;
        case "product_cycle_start":
          this.$store.dispatch("setProdCycleStart", { value, index });
          break;
        case "product_cycle_end":
          this.$store.dispatch("setProdCycleEnd", { value, index });
          break;
        default:
      }

      this.$store.dispatch("updateTotals");
    },
    addEclipseProduct() {
      this.$store.dispatch("addNewEclipseProduct");
    },
    removeProduct(index) {
      this.$store.dispatch("removeProduct", index);
    },
    handleImageFile(e) {
      const image = e.target.files[0];
      this.$store.dispatch("setProductImage", image);
    },
  },
};
</script>
